import type {ReactElement} from 'react';
import React, {useContext, useState} from 'react';
import {SocialProfileIcon, SocialProfileIconSize} from '@Components/social-media/components/social-profile-icon';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {getSocialPlatformDetailedName} from '@Libraries/social-media-platform.library';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import {useSocialPostPublishingAction, useWizardStep} from '@Components/social-media/social-media.hooks';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {getPosterPlaceholderImagePreviewURL, getPosterPlaceholderVideoPreviewURL} from '@Libraries/poster-library';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {useWizardDesignsGraphicsMetaMap} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import styles from './social-account-label.module.scss';
import {noop} from '@Utils/general.util';
import {getUserId} from '@Libraries/user.library';
import {Text, TextSize} from '@Components/text';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';

interface SocialAccountLabelProps {
  account: SocialAccount;
  showAccountDetails?: boolean;
  isDisabled?: boolean;
  className?: string;
  isConnectionExpired?: boolean;
  profileIconSize?: SocialProfileIconSize;
  accountNameClasses?: string;
  platformMediaType?: PlatformMediaType;
  onRemoveAccountClick?: VoidFunction;
}

function SocialAccountLabel({
  account,
  showAccountDetails = false,
  isDisabled = false,
  className = '',
  isConnectionExpired = false,
  profileIconSize,
  accountNameClasses = '',
  platformMediaType = PlatformMediaType.POST,
  onRemoveAccountClick = noop,
}: SocialAccountLabelProps): ReactElement<any> {
  const wizardStep = useWizardStep();
  const [isThumbnailLoading, setIsThumbnailLoading] = useState(false);
  const {isSmallScreen, isExtraSmallScreen} = useContext(SocialMediaWizardContainerQueriesContext);
  const designsGraphicsMetaMap = useWizardDesignsGraphicsMetaMap();
  const publishingAction = useSocialPostPublishingAction();

  const isFirstDesignAVideo = (): boolean => {
    const firstPosterId = Object.keys(designsGraphicsMetaMap)[0];
    const firstPageId = Object.keys(designsGraphicsMetaMap[firstPosterId])[0];
    return designsGraphicsMetaMap[firstPosterId][firstPageId].isVideo;
  };

  const getFirstDesignURL = (): string => {
    const firstPosterId = Object.keys(designsGraphicsMetaMap)[0];
    const firstPageId = Object.keys(designsGraphicsMetaMap[firstPosterId])[0];
    return designsGraphicsMetaMap[firstPosterId][firstPageId].graphicURL;
  };

  const isMultiPosting = (): boolean => {
    const posterIds = Object.keys(designsGraphicsMetaMap);

    if (posterIds.length > 1) {
      return true;
    }

    return Object.keys(designsGraphicsMetaMap[posterIds[0]]).length > 1;
  };

  const getMultiPostingIconIfNeeded = (): ReactElement<any> | null => {
    if (isMultiPosting()) {
      return <Icon className={styles.multiPostingIcon} icon="icon-copy" size={IconSize.SIZE_ICON_16} />;
    }

    return null;
  };

  const getDesignThumbnail = (): ReactElement<any> | null => {
    if (wizardStep === SocialMediaWizardStep.PUBLISH) {
      return (
        <div className={`${styles.designThumbContainer} ${isThumbnailLoading ? '_hidden' : ''} spacing-p-2`}>
          {getMultiPostingIconIfNeeded()}
          {isFirstDesignAVideo() ? (
            <video onLoadedData={onThumbnailAssetLoad} src={getFirstDesignURL()} className={styles.designThumb} />
          ) : (
            <img alt={window.i18next.t('pmwjs_design_thumbnail')} onLoad={onThumbnailAssetLoad} src={getFirstDesignURL()} className={styles.designThumb} />
          )}
        </div>
      );
    }

    return null;
  };

  const onThumbnailAssetLoad = (): void => {
    setIsThumbnailLoading(false);
  };

  const getDesignThumbnailLoading = (): ReactElement<any> | null => {
    if (wizardStep === SocialMediaWizardStep.PUBLISH && isThumbnailLoading) {
      return (
        <div className={`${styles.designThumbContainer} spacing-p-2`}>
          <CircularProgressLoader classes={styles.thumbLoader} color={LOADER_COLOR.DARK} size={LOADER_SIZE.XSMALL} />
          {isFirstDesignAVideo() ? (
            <video src={getPosterPlaceholderVideoPreviewURL()} className={styles.designThumbLoading} />
          ) : (
            <img alt={window.i18next.t('pmwjs_design_thumbnail')} src={getPosterPlaceholderImagePreviewURL()} className={styles.designThumbLoading} />
          )}
        </div>
      );
    }

    return null;
  };

  const showRemoveAccountCTA = (): boolean => {
    const isInEditingState = publishingAction === undefined;
    return wizardStep === SocialMediaWizardStep.ADD_DETAILS && isInEditingState;
  };

  const getRemoveAccountCTA = (): ReactElement<any> | null => {
    if (!showRemoveAccountCTA()) {
      return null;
    }

    return (
      <Icon icon="icon-delete" className="content-danger" size={IconSize.SIZE_ICON_20} type={IconType.GHOST_UNCOLORED} shape={IconShape.SQUARE} onClick={onRemoveAccountClick} />
    );
  };

  const getAccountNameTextElement = (): ReactElement<any> => {
    const classes = 'flex-h-row align-items-center';

    if (isConnectionExpired) {
      return (
        <div className={`${classes} gap-1`}>
          <span className={styles.accountNameText} title={account.name}>{account.name}</span>
          {getSharedInTeamElement()}
          <span> · </span>
          <span className="content-danger">{window.i18next.t('pmwjs_connection_expired')}</span>
        </div>
      );
    }

    return (
      <div className={`${classes} gap-2`}>
        <span className={styles.accountNameText} title={account.name}>{account.name}</span>
        {getSharedInTeamElement()}
      </div>
    );
  };

  const getSharedInTeamElement = (): ReactElement<any> | null => {
    if (account.idUser !== getUserId() && account.isSharedInTeam) {
      return (
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="top"
          delay={{show: 50, hide: 100}}
          overlay={
            <Tooltip id="tooltip-icon" className="spacing-m-b-3 spacing-m-l-1">
              <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_shared_in_team')} />
            </Tooltip>
          }
        >
          {
            <Icon
              className="flex-take-space"
              icon={'icon-team'}
              shape={IconShape.CIRCLE}
              type={IconType.FLAT}
              flatIconType={{color: FlatIconColor.PRIMARY, type: FlatIconColorType.DEFAULT}}
              size={IconSize.SIZE_ICON_12}
            />
          }
        </OverlayTrigger>
      );
    }

    return null;
  };

  const getExpiredAccountTextClasses = (): string => {
    return isConnectionExpired ? styles.hasExpirationText : '';
  };

  const getSocialMediaAccountLabels = (): ReactElement<any> => {
    if (!showAccountDetails) {
      return (
        <span className={`${styles.profileName} ${getExpiredAccountTextClasses()} ${getDisabledClass()} flex-vertical-center body-md-regular content-body spacing-m-l-3`}>
          {getAccountNameTextElement()}
        </span>
      );
    }

    return (
      <div className="flex-v-row">
        <span className={`${styles.profileName} ${getExpiredAccountTextClasses()} ${getDisabledClass()} body-md-bold content-body spacing-m-l-3 ${accountNameClasses}`}>
          {getAccountNameTextElement()}
        </span>
        <span className={`body-sm-regular spacing-m-l-3 ${getDisabledClass()}`}>{getSocialPlatformDetailedName(account.type)}</span>
      </div>
    );
  };

  const getDisabledClass = (): string => {
    return isDisabled ? styles.disabled : '';
  };

  const getProfileIconSize = (): SocialProfileIconSize => {
    if (profileIconSize) {
      return profileIconSize;
    }

    if (showAccountDetails) {
      return SocialProfileIconSize.LARGE;
    }

    return SocialProfileIconSize.MEDIUM;
  };

  return (
    <div className={`flex-h-row flex-items-center flex-justify-between ${className}`}>
      <div className="flex-h-row flex-items-center">
        <SocialProfileIcon platformMediaType={platformMediaType} account={account} isDisabled={isDisabled} size={getProfileIconSize()} isError={isConnectionExpired} />
        {getSocialMediaAccountLabels()}
      </div>
      {getRemoveAccountCTA()}
      {isSmallScreen || isExtraSmallScreen ? (
        <>
          {getDesignThumbnail()}
          {getDesignThumbnailLoading()}
        </>
      ) : null}
    </div>
  );
}

export default React.memo(SocialAccountLabel);
